import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Global from '../store/global-selectors';
import { messages } from '../components/country-names.messages';

import { useSortedTranslations } from './use-sorted-translations';

const generateCountryFirstSort = (targetCountry, countryPropName = 'code') => {
  return (a, b) => {
    if (a[countryPropName] === targetCountry) {
      return -1;
    }
    if (b[countryPropName] === targetCountry) {
      return 1;
    }
    return 0;
  };
};

/**
 * Helper function that consolidates the logic to get a list of countries translated and sorted according the users locale
 * @param {object} options - object that optionally contains arguments to specify how to perform the translations
 *    messageProp - the property name for the message descriptor to read from
 *    formatValuesProp - the property name for any values needed for formatting
 *    labelProp - the property name for the translated string to be written to
 * @returns {object[]} array of objects that represent a country that are sorted by the translated version of the country name and has the shape
 *    name - english name of the country
 *    code - two-letter country code
 *    label or options.labelProp - the translated name of the country
 */
export const useSortedTranslatedCountries = (options = {}) => {
  const countries = useSelector(Global.selectReferenceDatagetCountries);
  const countryObjs = useMemo(
    () =>
      countries.map(({ code, ...rest }) => ({
        code,
        ...rest,
        messageDescriptor: messages[code],
      })),
    [countries],
  );

  const homeCountryCode = useSelector(Global.selectSellerHomeCountry) || '';
  const hookOptions = useMemo(() => {
    return {
      ...options,
      prioritySort: generateCountryFirstSort(homeCountryCode),
    };
  }, [homeCountryCode, options]);

  return useSortedTranslations(countryObjs, hookOptions);
};
